import { format } from 'date-fns';
import { useEffect, useState } from 'react';
import {
  ComposedChart,
  Scatter,
  XAxis,
  YAxis,
  ZAxis,
  Cell,
  ReferenceLine,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import createTrend from 'trendline';

export default function ActionDetailsScatter({ data, assessment }) {
  let data5 = [];
  const [data6, setData6] = useState([]);
  const [xDomain, setXDomain] = useState([]);
  const [yDomain, setYDomain] = useState([]);

  const scores = data6?.map((data) => data?.score);
  const yMax = Math.max(...scores);
  const yMin = Math.min(...scores);
  const dates = data6?.map((data) => data?.date);
  const xMax = Math.max(...dates);
  const xMin = Math.min(...dates);

  const dateFormatter = (date) => {
    return format(new Date(date), 'MM/yyyy');
  };

  const tooltipFormatter = (date) => {
    return format(new Date(date), 'MM/dd/yyyy');
  };

  useEffect(() => {
    data?.forEach((d) => {
      data5 = [
        ...data5,
        {
          date: new Date(d.assessment_date.replace(/-/g, '/')).getTime(),
          score: d.assessment_score,
        },
      ];
    });
    setData6(data5);
    setXDomain([data6[0]?.date, data6[data6?.length - 1]?.date]);
    setYDomain([0, yMax]);
  }, [data]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className='custom-tooltip focus-ring-none rounded-md border-0 bg-white bg-opacity-80 p-4 font-medium text-brandText ring-0'>
          <p className='desc text-xl'>Check Details</p>
          <p className='label'>{`Date: ${tooltipFormatter(
            payload[0]?.payload?.date,
          )}`}</p>
          <p className='label'>{`Score: ${payload[0]?.payload?.score.toFixed(
            2,
          )}`}</p>
        </div>
      );
    }
    return null;
  };

  const getScatterColor = (d) => {
    if (d.score <= assessment?.score1_danger) {
      return '#dc2626';
    } else if (d.score <= assessment?.score1_warning) {
      return '#facc15';
    } else if (d.score >= assessment?.score1_success) {
      return '#84cc16';
    } else {
      return '#f1f5f9';
    }
  };

  const trendData = () => {
    const trend = createTrend(data6, 'date', 'score');

    return [
      { score: trend.calcY(xMin), date: xMin },
      { score: trend.calcY(xMax), date: xMax },
    ];
  };

  const [trendFirstCoord, trendLastCoord] = trendData();

  return (
    <div className='mx-auto flex h-96 w-full flex-col justify-center md:h-[450px] lg:h-[500px]'>
      <ResponsiveContainer width='100%' height='100%'>
        <ComposedChart
          width={400}
          height={400}
          data={data6}
          margin={{
            top: 20,
            right: 20,
            bottom: 0,
            left: 0,
          }}
        >
          <YAxis
            type='number'
            domain={yDomain}
            dataKey='score'
            name='Assessment Score'
            ticks={[0, (0 + yMax) / 2, yMax]}
            tickCount={3}
          />
          <XAxis
            type='number'
            domain={xDomain}
            tickFormatter={dateFormatter}
            scale={'time'}
            dataKey={'date'}
            name='Assessment Date'
          />
          <ZAxis dataKey={'score'} range={[250, 900]} />
          <Scatter
            name='Assessment'
            shape={'circle'}
            isAnimationActive={true}
            animationDuration={1500}
            dataKey={'score'}
            fill='#8884d8'
            z={1}
          >
            {data6?.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={getScatterColor(entry) ?? '#f1f5f9'}
              />
            ))}
          </Scatter>
          <ReferenceLine
            ifOverflow='extendDomain'
            segment={[
              { x: trendFirstCoord.date, y: trendFirstCoord.score },
              { x: trendLastCoord.date, y: trendLastCoord.score },
            ]}
            stroke='#0cb2da'
            strokeDasharray={'6, 2'}
          />
          <Tooltip
            content={CustomTooltip}
            cursor={{ strokeDasharray: '3 3' }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
